import { supabase } from "../supabaseClient";

/**
 * Normalize phone number by stripping formatting for comparison
 * +90 (532) 555 12 34 -> 905325551234
 * @param {string} phone - Formatted phone number
 * @returns {string} Normalized phone number (digits only)
 */
export const normalizePhone = (phone) => {
  if (!phone) return "";
  return phone.replace(/\D/g, "");
};

/**
 * Check if a customer already exists based on name, phone, or email.
 * Uses real Supabase queries against the customers table.
 *
 * @param {string} phone - Company phone number
 * @param {string} primaryPeoplePhone - Primary contact phone number
 * @param {string} companyName - Company name
 * @param {string} email - Company email
 * @returns {Promise<{ exists: boolean, message: string, matchType: string|null }>}
 */
export const checkCustomerExists = async (
  phone,
  primaryPeoplePhone,
  companyName,
  email
) => {
  const normalizedPhone = normalizePhone(phone);

  try {
    // Check duplicate name (case insensitive)
    if (companyName) {
      const { data: nameMatches, error: nameError } = await supabase
        .from("customers")
        .select("id, name")
        .ilike("name", companyName);

      if (nameError) {
        console.error("Error checking for duplicate name:", nameError);
        return { exists: false, message: "", matchType: null };
      }

      if (nameMatches && nameMatches.length > 0) {
        return {
          exists: true,
          message: `"${nameMatches[0].name}" adlı müşteri zaten sistemde kayıtlı.`,
          matchType: "name",
        };
      }
    }

    // Check duplicate phone or primary people phone
    if (phone || primaryPeoplePhone) {
      const orFilters = [];
      if (phone) orFilters.push(`phone.eq.${phone}`);
      if (primaryPeoplePhone)
        orFilters.push(`primary_people_phone.eq.${primaryPeoplePhone}`);

      const { data: phoneMatches, error: phoneError } = await supabase
        .from("customers")
        .select("id, name, phone, primary_people_phone")
        .or(orFilters.join(","));

      if (phoneError) {
        console.error("Error checking for duplicate phone:", phoneError);
        return { exists: false, message: "", matchType: null };
      }

      if (phoneMatches && phoneMatches.length > 0) {
        const match = phoneMatches[0];
        const matchedField =
          normalizePhone(match.phone) === normalizedPhone
            ? "phone"
            : "primaryPeoplePhone";
        return {
          exists: true,
          message:
            matchedField === "phone"
              ? `Bu telefon numarası zaten "${match.name}" müşterisine ait.`
              : `Bu yetkili telefon numarası zaten "${match.name}" müşterisine ait.`,
          matchType: matchedField,
        };
      }
    }

    // Check duplicate email (case insensitive)
    if (email) {
      const { data: emailMatches, error: emailError } = await supabase
        .from("customers")
        .select("id, name, email")
        .ilike("email", email);

      if (emailError) {
        console.error("Error checking for duplicate email:", emailError);
        return { exists: false, message: "", matchType: null };
      }

      if (emailMatches && emailMatches.length > 0) {
        return {
          exists: true,
          message: `Bu e-posta adresi zaten "${emailMatches[0].name}" müşterisine ait.`,
          matchType: "email",
        };
      }
    }

    return { exists: false, message: "", matchType: null };
  } catch (error) {
    console.error("Error in checkCustomerExists:", error);
    return { exists: false, message: "", matchType: null };
  }
};
